import React, { useState, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Divider,
  Chip,
  Button,
  Stack,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Eye, FileSpreadsheet, Search } from "lucide-react";
import * as XLSX from "xlsx";

import BackBtn from "../admin/widgets/BackBtn";
import useApiEndpoint from "../../events/hooks/useApiEndpoint";
import { API_ENDPOINT } from "../../../constants/constants";

const Leads = () => {
  const { data, isLoading, isError, error } = useApiEndpoint(
    `${API_ENDPOINT}user/get-scanned-users`
  );

  const [selectedLead, setSelectedLead] = useState(null);
  const [search, setSearch] = useState("");

  const handleView = (lead) => {
    setSelectedLead(lead);
  };

  const handleClose = () => {
    setSelectedLead(null);
  };

  // 🔍 Search Filter
  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((lead) => {
      const s = search.toLowerCase();
      return (
        lead.firstName.toLowerCase().includes(s) ||
        lead.lastName.toLowerCase().includes(s) ||
        lead.email.toLowerCase().includes(s) ||
        lead.companyName.toLowerCase().includes(s) ||
        lead.leadType.toLowerCase().includes(s)
      );
    });
  }, [search, data]);

  // 📤 EXPORT XLSX
  const exportToExcel = () => {
    if (!data || data.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
    XLSX.writeFile(workbook, "Leads.xlsx");
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3 }}>
      <BackBtn />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" fontWeight={600}>
          Leads
        </Typography>

        {/* EXPORT BUTTON */}
        <Button
          variant="contained"
          startIcon={<FileSpreadsheet size={18} />}
          onClick={exportToExcel}
        >
          Export All
        </Button>
      </Stack>

      {/* Search Bar */}
      <Box mb={2}>
        <TextField
          fullWidth
          placeholder="Search leads by name, email, company, or type..."
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <Search size={18} style={{ marginRight: 8 }} />,
          }}
        />
      </Box>

      {/* Loading */}
      {isLoading && (
        <Box display="flex" justifyContent="center" mt={3}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {isError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error?.message || "Failed to fetch leads"}
        </Alert>
      )}

      {/* TABLE */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <b>Name</b>
              </TableCell>
              <TableCell>
                <b>Company</b>
              </TableCell>
              <TableCell>
                <b>Email</b>
              </TableCell>
              <TableCell>
                <b>Lead Type</b>
              </TableCell>

              <TableCell align="center">
                <b>Actions</b>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData?.map((lead) => (
              <TableRow key={lead._id} hover>
                <TableCell>
                  {lead.firstName} {lead.lastName}
                </TableCell>
                <TableCell>{lead.companyName}</TableCell>
                <TableCell>{lead.email}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={lead.leadType}
                    sx={{
                      bgcolor:
                        lead.leadType === "Hot"
                          ? "error.light"
                          : lead.leadType === "Warm"
                          ? "warning.light"
                          : "info.light",
                    }}
                  />
                </TableCell>

                <TableCell align="center">
                  <IconButton onClick={() => handleView(lead)}>
                    <Eye size={20} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {filteredData.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  No leads found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* VIEW DETAILS DIALOG */}
      <Dialog
        open={!!selectedLead}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Lead Details</DialogTitle>
        <Divider />

        <DialogContent sx={{ mt: 2 }}>
          {selectedLead && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>

              <Grid item xs={6}>
                <Typography>
                  <b>Name:</b> {selectedLead.firstName} {selectedLead.lastName}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography>
                  <b>Email:</b> {selectedLead.email}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography>
                  <b>Company:</b> {selectedLead.companyName}
                </Typography>
              </Grid>

              {/* Lead Type */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Lead Type
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Chip
                  label={selectedLead.leadType}
                  sx={{
                    fontWeight: 600,
                    bgcolor:
                      selectedLead.leadType === "Hot"
                        ? "error.light"
                        : selectedLead.leadType === "Warm"
                        ? "warning.light"
                        : "info.light",
                  }}
                />
              </Grid>

              {/* Comment */}
              {selectedLead.comment && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Comment
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Box
                    p={2}
                    borderRadius={2}
                    bgcolor="#f5f7fa"
                    border="1px solid #e0e0e0"
                  >
                    {selectedLead.comment}
                  </Box>
                </Grid>
              )}

              {/* Extra */}
              <Grid item xs={12} mt={1}>
                <Typography>
                  <b>Barcode:</b> {selectedLead.barcode_id}
                </Typography>
                <Typography>
                  <b>Username:</b> {selectedLead.username}
                </Typography>
                <Typography>
                  <b>Created:</b>{" "}
                  {new Date(selectedLead.createdOn).toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          )}

          <Stack direction="row" justifyContent="flex-end" mt={3}>
            <Button variant="contained" onClick={handleClose}>
              Close
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Leads;
